import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ModalComponent} from '../../../../core/components/modal/modal.component';
import {Booking} from '../../../../core/entities/booking/booking.entity';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';

import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ServiceService} from '../../../services/service/service.service';
import {Service} from '../../../../core/entities/service/service.entity';
import {ToastrService} from 'ngx-toastr';
import {BookingService} from '../../../services/booking/booking.service';
import {DateTime} from 'luxon';
import {ToggleComponent} from '../../../../core/components/toggle/toggle.component';

@Component({
  selector: 'app-create-booking-modal',
  imports: [
    ModalComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    ToggleComponent
],
  templateUrl: './create-booking-modal.component.html',
  styleUrl: './create-booking-modal.component.scss'
})
export class CreateBookingModalComponent implements OnInit {
  @Input() open = false;
  @Input() booking: Booking|null = null;
  @Output() closed = new EventEmitter<Booking|null>();
  @Output() deleted = new EventEmitter<Booking|null>();

  public form: FormGroup;
  public services: Service[] = [];
  public minDateTime: string = '';
  public selectedService: Service|null = null;

  public constructor(
    private readonly fb: FormBuilder,
    private readonly bookingService: BookingService,
    private readonly serviceService: ServiceService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
  ) {
    this.form = this.fb.group({
      customerEmail: ['', [Validators.required, Validators.email]],
      serviceId: ['', Validators.required],
      datetime: ['', Validators.required],
      deposit: [null],
    });

    const now = new Date();
    this.minDateTime = now.toISOString().slice(0, 16); // format: yyyy-MM-ddThh:mm
  }

  public ngOnInit() {
    this.form.get('serviceId')?.valueChanges.subscribe((serviceId) => {
      this.syncSelectedService(serviceId);
    });

    this.serviceService.getServices().subscribe(
      (services) => {
        this.services = services;
        this.syncSelectedService(this.form.get('serviceId')?.value ?? null);
        // If this.services.length == 0, show a little explanation below the service select field
        // that says the photographer needs at least one available service to add a booking
      }
    );
  }

  public create() {
    if (this.form.invalid) {
      this.toastr.error(
        this.transloco.translate('bookings.create-modal.errors.fill-fields.content'),
        this.transloco.translate('bookings.create-modal.errors.fill-fields.title')
      );
      return;
    }

    let { customerEmail, serviceId, datetime, deposit } = this.form.getRawValue();

    // Convert datetime to Luxon DateTime
    if (!(datetime instanceof DateTime)) {
      datetime = DateTime.fromISO(datetime);
    }

    this.bookingService.createBooking(
      customerEmail,
      null,
      null,
      serviceId,
      datetime,
      deposit
    ).subscribe(
      (booking) => {
        this.toastr.success(
          this.transloco.translate('bookings.create-modal.success.content'),
          this.transloco.translate('bookings.create-modal.success.title')
        );
        this.close(booking);
      }, () => {
        this.toastr.error(
          this.transloco.translate('bookings.create-modal.errors.failed-add.content'),
          this.transloco.translate('bookings.create-modal.errors.failed-add.title')
        )
      }
    )
  }

  public close(booking: Booking|null = null) {
    setTimeout(() => {
      this.form.reset(); // Prevent the reset to happen before the modal animation ends
      this.selectedService = null;
      this.resetDeposit();
    },250);
    this.closed.emit(booking);
  }

  public onDepositToggle(allowDeposit: boolean) {
    if (!this.selectedService || !this.selectedService.allowDeposit || !this.selectedService.depositPercentage) {
      this.resetDeposit();
      return;
    }

    this.form.patchValue({
      deposit: allowDeposit ? this.selectedService.depositPercentage : null
    }, { emitEvent: false });
  }

  private syncSelectedService(serviceId: number | string | null) {
    if (serviceId === null || serviceId === '') {
      this.selectedService = null;
      this.resetDeposit();
      return;
    }

    const numericId = typeof serviceId === 'number' ? serviceId : Number(serviceId);
    if (Number.isNaN(numericId)) {
      this.selectedService = null;
      this.resetDeposit();
      return;
    }

    this.selectedService = this.services.find((service) => service.id === numericId) ?? null;
    this.resetDeposit();
  }

  private resetDeposit() {
    this.form.patchValue({ deposit: null }, { emitEvent: false });
  }
}
